import { ColDef, GetRowIdParams } from "ag-grid-community";
import { RowNode, Column } from "ag-grid-community";
import {
  GetDataPath,
  GetRowIdFunc,
} from "ag-grid-community/dist/types/src/entities/gridOptions";
import type { RowDragEndEvent } from "ag-grid-community/dist/types/src/events";
import { ValueChangedEvent } from "@shared/type/CommonType";

export interface RowData {
  keyField?: string;
  state?: string;
  [key: string]: any;
}

export interface SnGridProps {
  columnConfig: ColumnConfig[]; // 컬럼 정의
  rowData: RowData[] | null; // 행 데이터
  enablePasteOnlyAdd?: boolean; // 26.03.16 kdy 클립보드 붙여넣기 (기존행은 데이터는 유지하면서 행추가 허용)
  useTreeTempKey?: boolean; // 26.05.07 kdy 트리 TempKey 사용유무
  etcProps?: SnGridEtcProps;
  events?: SnGridEvents;
  treeProps?: SnTreeEtcProps;
  treeEvents?: SnTreeEvents;
  filter?: { refs?: any[]; columns?: string[] };
  header?: React.ReactNode; // 그리드 헤더
  popupParent?: any; // 부모 엘리먼트
  headerInfo?: { controls: any[]; data: any };
  pinnedTopRowData?: any[];
}

export interface SnGridEtcProps {
  popupParent?: any; // 부모 엘리먼트
  isEditable?: boolean; // 셀 편집 가능 여부를 제어하는 콜백 함수
  title?: string; // 그리드 제목
  rowSelection?: "single" | "multiple"; // 행 선택 모드
  ischeckboxSelection?: boolean; // 체크박스 셀렉션
  rowGroupPanelShow?: "onlyWhenGrouping" | "never" | "always";
  entertoAddrow?: boolean;
  enableInitialSort?: boolean; // 조회 직후 기본 정렬 적용 여부 (default: true)
  suppressInitialCellFocus?: boolean; // 조회 후 행 선택은 하되 DOM 포커스는 뺏지 않음 (공통팝업에서 SearchPanel 포커스 유지용)
  getRowStyle?: any;
  /**
   * 셀 값의 줄바꿈(\n)을 그대로 표시하고 행 높이를 내용에 맞춘다.
   * 조회 SQL 에서 여러 건을 chr(10) 으로 묶어 한 셀에 넣는 화면에서 켠다. 기본값 off.
   */
  wrapText?: boolean;
  extraHeaderMenuItems?: ExtraHeaderMenuItem[]; // 헤더 우클릭에 추가할 메뉴 항목 목록
  /**
   * 지정하면 No. 컬럼에 드래그 핸들이 생기고, 놓은 순서대로 이 컬럼에 1부터 순번을 다시 부여한다.
   * (예: "no_seq") 트리(SnTree)와 달리 필터가 걸린 그리드에서도 동작한다.
   */
  rowDragOrderKey?: string;
  /**
   * 26.09.07 kdy 헤더를 그리드 밖으로 끌어냈을 때 컬럼이 숨겨지는 AG Grid 기본 동작을 막는다.
   * (태블릿에서 터치 스크롤 중 컬럼이 사라지는 문제) 컬럼 순서 드래그 이동은 그대로 가능. 기본값 off.
   */
  suppressDragLeaveHidesColumns?: boolean;
  /**
   * 26.09.22 kdy 하단 합계(서머리) 행 표시 여부. 기본값 true.
   * yn_sum 컬럼이 없어도 건수 행은 항상 보이며, false 로 주면 행 자체를 숨긴다.
   */
  showFooter?: boolean;
  /**
   * 헤더 메뉴의 "항목설정저장" · "항목 초기화"를 숨긴다. 기본값 off.
   * 대시보드 위젯 상세처럼 컬럼 정의가 sys_formgridcolumn 이 아닌 곳(sys_widgetgridcolumn)에 있는 그리드에서 켠다.
   */
  disableColumnSetting?: boolean;
  /**
   * 26.11.04 kdy 컬럼 가상화를 끈다. 기본값 off.
   * autoSizeAllColumns 는 DOM 에 그려진 컬럼만 재므로, 화면 밖 컬럼까지 폭을 맞춰야 하는 곳(데이터 인스펙터)에서 켠다.
   * 컬럼이 많으면 렌더 비용이 커지니 일반 화면에서는 쓰지 않는다.
   */
  suppressColumnVirtualisation?: boolean;
}

// 헤더 우클릭 메뉴에 동적으로 붙일 항목 정의
export interface ExtraHeaderMenuItem {
  id: string; // 메뉴 동작 식별자 (화면에서 id 기준으로 분기)
  name: string; // 메뉴 표시명
  visible?: boolean; // 노출 여부 (기본: true)
}

export interface SnGridEvents {
  sumRowFilter?: (row: any) => boolean; // footer 집계 대상 필터
  onCellClicked?: (params: any) => void; // 26.06.29 kdy 태블릿 같은 경우 넘버패드 여는거 때문에 추가함
  onCellFocused?: () => void; // 셀 포커스 이후 이벤트 26.03.20 kdy 바코드 때문에 추가함
  onExcelExport?: (params: any) => void; // context menu export override
  getContextMenuItems?: (params: any) => any[]; // context menu override
  onBtnAddClick?: (id: string) => void; // 행 추가 버튼 클릭 이벤트 핸들러
  onBtnDeleteClick?: (id: string) => void; // 행 삭제 버튼 클릭 이벤트 핸들러
  onCellValueChanged?: (event: CellValueChangedEvent) => void; // 셀 값 변경 이벤트 핸들러
  onRowDataReady?: () => void; // 행 데이터 준비 완료 이벤트 핸들러
  onFocusedRowChanged?: (event: any) => void; // 포커스된 행 변경 이벤트 핸들러
  onContextMenu?: (event: any) => void; // 우클릭 이벤트 핸들러
  onRowDoubleClicked?: (event: any) => void; // 행 더블클릭 이벤트 핸들러
  onBeforeAddRow?: () => boolean | Record<string, any>; //행이 그리드에 들어가기 직전에 한 번 호출
  onAfterSetFooter?: (event: Record<string, number>) => void; //footer 셋팅이후 호출
  onBeforeSetFooter?: (
    footer: Record<string, any>
  ) => Record<string, any> | void; //footer 셋팅 직전 호출, 반환값으로 합계 값 재정의 가능
  onCheckedRowChaged?: (value: boolean, type?: "row" | "header") => void; //체크된 행 변경될 때 호출
  onCellDoubleClicked?: (event: any) => void; // 셀 더블클릭 이벤트 핸들러
  onHeaderValueChanged?: (
    event: ValueChangedEvent
  ) => Record<string, any> | void;
  onHeaderEnter?: (sender: string, value: string) => void;
  onBtnEtcClick?: (sender: string) => void;
  onAfterPopupClose?: (event: { nodes: RowNode[]; colId: string }) => void;
  onSortChanged?: () => void; // 그리드 sorting 이후 실행
  onFilterChanged?: () => void; // 그리드 filtering 이후 실행
  onModelUpdated?: () => void; //뭔가 변경됐을 떄 호출됨
  onExtraHeaderMenuClick?: (event: { id: string; params: any }) => void; // 추가 헤더 메뉴 클릭 이벤트
  onAfterAddRow?: (node: RowNode) => void; // 행 추가 후 호출되는 이벤트
  onRowDataUpdated?: () => void; // 26.07.09 kdy 행 데이터 업데이트 이벤트
  onRowDragEnd?: (event: RowDragEndEvent) => void; // 행 드래그로 순서 변경 완료 후 호출
}

export interface SnTreeEtcProps {
  treeDataParentIdField?: string;
  treeIdKey?: string;
  treeOrderKey?: string;
  groupDefaultExpanded?: number;
  autoGroupColumnDef?: any;
  getRowId?: GetRowIdFunc;
  getDataPath?: GetDataPath;
}

export interface SnTreeEvents {
  onBtnAddClick?: (id: string) => void; // 행 추가 버튼 클릭 이벤트 핸들러
  onBtnDeleteClick?: (id: string) => void; // 행 삭제 버튼 클릭 이벤트 핸들러
  onFocusedRowChanged?: (event: any) => void; // 포커스된 행 변경 이벤트 핸들러
  onRowDragEnd?: (event: RowDragEndEvent) => void;
  onBeforeAddRow?: () => boolean | Record<string, any>; //행이 그리드에 들어가기 직전에 한 번 호출
  onAfterSetFooter?: (event: Record<string, number>) => void; //footer 셋팅이후 호출
  onCheckedRowChaged?: (value: boolean) => void; //체크된 행 변경될 때 호출
}

export interface CellValueChangedEvent {
  newValue: any;
  oldValue: any;
  colId: string;
  rowData: any;
  rowIndex: number;
  rowNode: RowNode;
}

// 컬럼 JSON 구조를 정의
export interface ColumnConfig {
  cd_firm: string;
  cd_menu: string;
  cd_grid: string;
  cd_user: string;
  cd_column: string;
  nm_column: string;
  tp_column: string;
  fg_visible: string;
  cd_displaycolumn?: string | null;
  cd_popup?: string | null;
  no_seq: number;
  qt_width?: number;
  yn_required?: string | null;
  yn_allowsorting?: string | null;
  yn_fixed?: string | null;
  tp_fixedposition?: string | null;
  qt_groupindex?: number;
  yn_sum?: string | null;
  yn_system?: string | null;
  tp_format?: string | null;
  dc_editcon?: string | null;
  yn_drilldown?: string | null;
  dc_filter?: string | null;
  nm_group?: string | null;
  js_combo?: string | null;
  popup_apply?: [];
  cd_clas?: string | null;
  cd_value?: string | null; // 26.06.04 kdy 신규등록시 디폴트 값
  valueFormatter?: (params: any) => any; // 사용자 정의 값 포맷터 함수
  cellStyle?: any; // 화면에서 지정하는 셀 스타일 (정렬 등)
  /** 26.10.07 kdy 행 데이터에 따라 컬럼 타입을 바꾼다 (C# GridView CustomRowCellEdit 대응).
   *  예) 통제환경설정 cd_flag: tp_ctrl 이 D 인 행만 날짜. undefined 를 돌려주면 tp_column 그대로. */
  tpColumnByRow?: (data: any) => RowColumnType | undefined;
}

/** tpColumnByRow 가 돌려줄 수 있는 행별 컬럼 타입 */
export type RowColumnType = "SnDateBox" | "SnTextBox" | "SnCodeBox";

export interface CustomColDef extends ColDef {
  codeValueChanged?: (params: any) => void;
  columnConfig: {
    cd_grid: string;
    cd_menu: string;
    cd_column: string;
    cd_displaycolumn?: string | null;
    nm_column: string;
    fg_visible: string;
    tp_column?: string | null;
    yn_drilldown?: string | null;
    yn_sum?: string | null;
    dc_editcon?: string | null;
    tp_format?: string | null;
    cd_clas?: string | null;
    js_combo?: string | null;
    cd_value?: string | null;
    tpColumnByRow?: (data: any) => RowColumnType | undefined;
  };
}

export type ChangeColumnConfig = {
  cd_column: string;
  nm_column: string;
  fg_visible: string;
  js_combo?: string;
};

export type FlatRowType = Record<string, FlatRowCell>;

export type FlatRowCell = {
  value: string | number;
  dataType: "string" | "number" | "dateTime";
};

export type ChangeGroupColumnConfig = {
  nm_header: string;
  cd_column: string;
  nm_column: string;
  fg_visible: string;
};

export type SnGridRef = {
  /** 컬럼이 AG Grid에 등록되면 resolve. 등록 전 setDataValue는 무시되므로 드릴다운 등 프로그램 매핑 전에 대기용. */
  whenColumnsReady: () => Promise<void>;

  /** preferNewRow=true: onBeforeAddRow 훅·컬럼기본값이 덮어도 최종적으로 newRow 값이 이긴다(복제용).
   *  addIndex: 원본 데이터 순서 기준 삽입 위치(필터/정렬 무관). 생략하면 맨 끝에 추가. */
  addRow: (
    newRow?: RowData,
    useEvent?: boolean,
    preferNewRow?: boolean,
    addIndex?: number
  ) => RowNode | null;

  /** * 26.02.05 kdy
   * * 체크박스 초기화
   */
  clearCheckBox: () => void;

  /** * 26.02.02 kdy
   * * 컬럼 표시 순서 정보가 바뀌는 하는 경우
   */
  changeColumnReorderConfigs: (columnSettings: ChangeColumnConfig[]) => void;

  deleteSelectedRows: () => void;
  deleteRowByIndex: (index: number) => void;
  deleteAllRows: () => void;

  getChange: () => {
    modifiedRowcount: number;
    insert: any[];
    update: any[];
    delete: any[];
  };
  resetUpdate: (isRecovery?: boolean) => void;

  selectAndFocusRow: (index: number, skipCellFocus?: boolean) => void;
  selectAllFiltered: () => void;

  getSelectedRows: () => any[];
  getRangeSelectedRows: () => any[]; // 셀 범위(드래그) 선택이 덮은 행들

  /** * 26.04.30 kdy
   * * grid 체크박스 체크된 RowCount 반환
   */
  getCheckedRowCount: () => number;

  /** * 26.05.13 kdy
   * * grid 필터된 데이터 중 체크박스 체크된 RowCount 반환
   */
  getFilteredCheckedRowCount: () => number;

  /** * 26.05.13 kdy
   * * grid 포커스 노드 체크박스 체크 여부 반환
   */
  getFocusedRowChecekd: () => boolean;

  getDisplayedRowCount: () => number;

  /** * 26.02.11 kdy
   * * grid 필터 안 된 전체 RowCount 반환
   */
  getRowCount: () => number;

  /** * 26.02.02 kdy
   * * grid 필터 안 된 전체 레코드 중 가장 큰 값 반환
   */
  getNonFilterMaxValue: (column: string) => number;

  stopEditing: () => void;

  changeColumnConfigs: (columnSettings: ChangeColumnConfig[]) => void;
  changeGroupColumnConfigs: (columnSettings: ChangeGroupColumnConfig[]) => void;

  requiredValuesCheck: () => boolean;

  applyBlock: (node: RowNode) => void;
  clearBlock: (node: RowNode) => void;

  getMaxValue: (column: string) => number;

  getCheckedRows: () => any[];

  applyFilters: (filterConditions: { [key: string]: any }) => void;

  /** 26.09.23 kdy 상위 그리드 필터 결과가 0행일 때 하위 체인을 비우는 용도 (내부 전파용) */
  applyNoMatchFilter: () => void;

  /** * 26.02.05 kdy
   * * 퀵필터를 이용한 like 필터 2글자입력해야 제대로 작동해서 예외처리돼있음
   */
  applyLikeFilters: (
    filterConditions: { [key: string]: any },
    orFields?: string[]
  ) => void;

  resetFilters: () => void;

  getFooterData: () => any;
  calculateTotals: () => void;

  getFlatDataFromGrid: (columnKeys: string[]) => Map<string, FlatRowType>;

  applyPopupConditions: (popupConditions: any) => void;

  sharedCellValueChanged: (change: any) => void;

  focusedRowNode: () => RowNode;

  getRowNodeAtIndex: (index: number) => RowNode;
  getRowNode: (key: string) => RowNode;

  getAllGridColumns: () => Column[];
  getAllDisplayedColumns: () => Column[];
  autoSizeAllColumns: (skipHeader?: boolean) => void;
  sizeColumnsToFit: () => void;

  /** * 26.02.05 kdy
   * * footer 다시 그리기
   */
  refreshFooter: () => void;

  resetGrid: () => void;

  forEachFilteredNode: (callback: (node: RowNode) => void) => void;

  forEachNode: (callback: (node: RowNode) => void) => void;

  scrollToIndex: (index: number) => void;
  setFocusedCell: (index: number, colId: string) => void;

  clearFocus: () => void;

  exportDataAsExcel: (params?: any) => void;

  exportMultipleSheetsAsExcel: (params?: any) => void;

  getSheetDataForExcel: (params?: any) => void;

  applyTransaction: (params: any) => void;

  setCheckedAll: (check: boolean) => void;
  setCheckedAllWithFilter: (check: boolean) => void;

  setCheckedFocusedRow: (check: boolean, source?: string) => void;
  reverseCheckedRow: (node: RowNode, source?: string) => void;

  setCheckedRow: (node: RowNode, check: boolean, source?: string) => void;
  getCheckedFilteredRows: () => any[];

  /** 현재 적용된 정렬 상태 [{colId, sort}] (정렬 없으면 빈 배열) */
  getSortModel: () => { colId: string; sort: "asc" | "desc" }[];

  getFilteredData: () => any[];

  deleteRowByNode: (node: RowNode) => void;
};

export const TpColumnList = [
  "SnTextBox",
  "SnNumberBox",
  "SnMaskBox",
  "SnDateBox",
  "SnCodeBox",
  "SnComboBox",
  "SnMemoBox",
  "SnCheckBox",
  "SnMultiCodeBox",
  "SnButtonBox",
  "SnPasswordBox",
] as const;
